/**
 * ConfigDetailPage - 配置详情展示页面
 * 扫描二维码后展示的配置详情，展示用户的配置媒体或360度摩托车展示
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import styles from './ConfigDetailPage.module.css';

// 配件名称映射
const PART_NAMES = {
    // Body Colors
    dark_billiard_gray: 'Dark Billiard Gray',
    vivid_black: 'Vivid Black',
    white_onyx_pearl: 'White Onyx Pearl',
    brilliant_red: 'Brilliant Red',
    olive_steel_metallic_vivid_black: 'Olive Steel / Vivid Black',
    // Trim
    chrome: 'Chrome Style',
    black_finish: 'Black Finish',
    // Comfort
    sissy_bar_upright: 'Sissy Bar Upright',
    get_a_grip_handgrips_chrome: 'Get-A-Grip Handgrips',
    detachable_saddlebags: 'Detachable Saddlebags',
    detachable_windshield: 'Detachable Windshield',
    hammock_comfort_seat: 'Hammock Comfort Seat',
    engine_guard: 'Engine Guard Kit',
    long_haul_flfb: 'Long Haul Package',
    // Performance
    none: 'Stock',
    heavy_breather_air_cleaner_chrome: 'Heavy Breather Air Cleaner',
    wedge_air_cleaner_chrome: 'Wedge Air Cleaner',
    round_air_cleaner: 'Round Air Cleaner',
    vh_hi_output_slip_on: 'Hi-Output Slip-On',
    // Style
    empire_collection: 'Empire Collection',
    led_daymaker: 'LED Daymaker Headlamp',
    custom_covers: 'Custom Covers Set'
};

// 分类名称映射
const CATEGORY_NAMES = {
    body: 'Body Color',
    trim: 'Trim Style',
    comfort: 'Comfort',
    performance: 'Performance',
    style: 'Style'
};

// 颜色代码映射
const COLOR_CODE_MAP = {
    'dark_billiard_gray': 'm85',
    'vivid_black': 'm04',
    'white_onyx_pearl': 'm07',
    'brilliant_red': 'm44',
    'olive_steel_metallic_vivid_black': 'm78'
};

const TOTAL_FRAMES = 28;

export default function ConfigDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 360度旋转状态
    const [currentFrame, setCurrentFrame] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isAutoRotating, setIsAutoRotating] = useState(true);
    const startX = useRef(0);
    const autoRotateRef = useRef(null);

    // 媒体轮播状态
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

    // 获取配置详情
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
                const response = await fetch(`${API_BASE_URL}/configurations/${id}`);
                const result = await response.json();

                if (result.success) {
                    setConfig(result.data);
                } else {
                    setError(result.error || '配置不存在');
                }
            } catch (err) {
                console.error('Fetch config error:', err);
                setError('加载失败，请稍后重试');
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, [id]);

    // 检查是否有用户上传的媒体
    const hasUserMedia = config?.media && config.media.length > 0;
    const hasCoverImage = config?.cover_image_url;
    const has3DModel = config?.model3d_url;

    // 获取所有可展示的媒体
    const getDisplayMedia = () => {
        const media = [];

        // 优先使用封面图
        if (hasCoverImage) {
            media.push({ type: 'image', url: config.cover_image_url });
        }

        // 添加用户上传的媒体
        if (hasUserMedia) {
            config.media.forEach(m => {
                media.push({ type: m.media_type, url: m.url, thumbnail: m.thumbnail_url });
            });
        }

        return media;
    };

    const displayMedia = config ? getDisplayMedia() : [];
    const useCustomMedia = displayMedia.length > 0;

    // 自动旋转 (仅在没有自定义媒体时使用360度展示)
    useEffect(() => {
        if (!useCustomMedia && isAutoRotating && config) {
            autoRotateRef.current = setInterval(() => {
                setCurrentFrame(prev => (prev + 1) % TOTAL_FRAMES);
            }, 150);
        }
        return () => {
            if (autoRotateRef.current) {
                clearInterval(autoRotateRef.current);
            }
        };
    }, [isAutoRotating, config, useCustomMedia]);

    // 媒体轮播自动切换
    useEffect(() => {
        if (useCustomMedia && displayMedia.length > 1 && isAutoRotating) {
            const interval = setInterval(() => {
                setCurrentMediaIndex(prev => (prev + 1) % displayMedia.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [useCustomMedia, displayMedia.length, isAutoRotating]);

    // 获取颜色代码
    const getColorCode = () => {
        const bodyColor = config?.config_data?.body;
        return COLOR_CODE_MAP[bodyColor] || 'm85';
    };

    // 获取360图片路径
    const get360FramePath = (frameIndex) => {
        const colorCode = getColorCode();
        const paddedIndex = String(frameIndex + 1).padStart(2, '0');
        return `/assets/360/2026-fat-boy-${colorCode}-motorcycle/2026-fat-boy-${colorCode}-motorcycle-${paddedIndex}.jpg`;
    };

    // 拖拽旋转处理 (360度展示)
    const handleMouseDown = (e) => {
        if (useCustomMedia) return;
        setIsDragging(true);
        setIsAutoRotating(false);
        startX.current = e.clientX || e.touches?.[0]?.clientX;
    };

    const handleMouseMove = (e) => {
        if (!isDragging || useCustomMedia) return;
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        const delta = clientX - startX.current;
        if (Math.abs(delta) > 15) {
            const direction = delta > 0 ? -1 : 1;
            setCurrentFrame(prev => {
                let next = prev + direction;
                if (next < 0) next = TOTAL_FRAMES - 1;
                if (next >= TOTAL_FRAMES) next = 0;
                return next;
            });
            startX.current = clientX;
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // 分享功能
    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: config?.title || 'My Fat Boy Configuration',
                    text: 'Check out my custom Harley-Davidson Fat Boy configuration!',
                    url: url
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            try {
                await navigator.clipboard.writeText(url);
                alert('链接已复制！ / Link copied!');
            } catch (err) {
                console.error('Copy failed:', err);
            }
        }
    };

    // 继续定制 - 将配置传递到3D预览页面
    const handleContinueCustomizing = () => {
        if (!config?.config_data) return;

        // 导航到3D预览页面，传递配置数据
        navigate('/3d-preview', {
            state: {
                presetConfig: config.config_data,
                fromConfigDetail: true,
                configTitle: config.title
            }
        });
    };

    // 获取配件名称
    const getPartName = (partId) => {
        return PART_NAMES[partId] || partId;
    };

    // 渲染配置项
    const renderConfigItems = () => {
        if (!config?.config_data) return [];

        const configData = config.config_data;
        const items = [];

        // Body Color
        if (configData.body) {
            items.push({
                category: 'body',
                name: getPartName(configData.body),
                isCustom: configData.customization?.selectedColor === 'custom-color'
            });
        }

        // Trim
        if (configData.trim) {
            items.push({
                category: 'trim',
                name: getPartName(configData.trim)
            });
        }

        // Comfort (array)
        if (configData.comfort && configData.comfort.length > 0) {
            configData.comfort.forEach(partId => {
                items.push({
                    category: 'comfort',
                    name: getPartName(partId)
                });
            });
        }

        // Performance (可能是字符串或数组)
        if (configData.performance) {
            const perfItems = Array.isArray(configData.performance)
                ? configData.performance
                : [configData.performance];
            perfItems.forEach(partId => {
                if (partId && partId !== 'none') {
                    items.push({
                        category: 'performance',
                        name: getPartName(partId)
                    });
                }
            });
        }

        // Style (array)
        if (configData.style && configData.style.length > 0) {
            configData.style.forEach(partId => {
                items.push({
                    category: 'style',
                    name: getPartName(partId)
                });
            });
        }

        return items;
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading configuration...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <h2>Oops!</h2>
                    <p>{error}</p>
                    <Link to="/3d-preview" className={styles.ctaBtn}>
                        Create Your Own
                    </Link>
                </div>
            </div>
        );
    }

    const configItems = renderConfigItems();
    const pageUrl = window.location.href;

    return (
        <div className={styles.container}>
            {/* 摩托车展示区域 - 全屏震撼 */}
            <div
                className={styles.bikeShowcase}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={(e) => handleMouseDown(e.touches[0])}
                onTouchMove={(e) => handleMouseMove(e.touches[0])}
                onTouchEnd={handleMouseUp}
            >
                {/* 背景光效 */}
                <div className={styles.showcaseGlow}></div>

                {/* 展示内容 - 根据是否有自定义媒体决定 */}
                {useCustomMedia ? (
                    // 用户上传的媒体展示
                    <div className={styles.mediaContainer}>
                        {displayMedia[currentMediaIndex]?.type === 'video' ? (
                            <video
                                src={displayMedia[currentMediaIndex].url}
                                className={styles.mediaVideo}
                                autoPlay
                                loop
                                muted
                                playsInline
                            />
                        ) : (
                            <img
                                src={displayMedia[currentMediaIndex]?.url}
                                alt="Custom Configuration"
                                className={styles.mediaImage}
                            />
                        )}

                        {/* 媒体导航点 */}
                        {displayMedia.length > 1 && (
                            <div className={styles.mediaDots}>
                                {displayMedia.map((_, idx) => (
                                    <button
                                        key={idx}
                                        className={`${styles.mediaDot} ${idx === currentMediaIndex ? styles.active : ''}`}
                                        onClick={() => {
                                            setCurrentMediaIndex(idx);
                                            setIsAutoRotating(false);
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* 自定义配置标签 */}
                        <div className={styles.customBadge}>
                            <span>✨ CUSTOM BUILD</span>
                        </div>
                    </div>
                ) : (
                    // 360度旋转展示
                    <>
                        <img
                            src={get360FramePath(currentFrame)}
                            alt="Harley-Davidson Fat Boy"
                            className={styles.bikeImage}
                            draggable="false"
                        />

                        {/* 旋转提示 */}
                        <div className={styles.rotateHint}>
                            <span className={styles.rotateIcon}>↻</span>
                            <span>Drag to Rotate</span>
                        </div>
                    </>
                )}

                {/* 价格悬浮标签 */}
                <div className={styles.priceTag}>
                    <span className={styles.priceLabel}>MSRP</span>
                    <span className={styles.priceValue}>
                        ${config.total_price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                </div>

                {/* 自动播放控制 */}
                <button
                    className={`${styles.autoRotateBtn} ${isAutoRotating ? styles.active : ''}`}
                    onClick={() => setIsAutoRotating(!isAutoRotating)}
                >
                    {isAutoRotating ? '⏸' : '▶'}
                </button>
            </div>

            {/* 配置标题 */}
            <div className={styles.titleSection}>
                <h1 className={styles.title}>{config.title}</h1>
                <p className={styles.subtitle}>CUSTOM CONFIGURATION</p>
                {config.user && (
                    <div className={styles.creatorBadge}>
                        <span>by {config.user.nickname || 'Anonymous'}</span>
                    </div>
                )}
            </div>

            {/* 配置详情卡片 */}
            <div className={styles.detailsCard}>
                <h2 className={styles.cardTitle}>Configuration</h2>
                <div className={styles.configGrid}>
                    {configItems.map((item, index) => (
                        <div key={index} className={styles.configChip}>
                            <span className={styles.chipCategory}>
                                {CATEGORY_NAMES[item.category] || item.category}
                            </span>
                            <span className={styles.chipValue}>{item.name}</span>
                            {item.isCustom && <span className={styles.customTag}>Custom</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* 3D模型查看提示 */}
            {has3DModel && (
                <div className={styles.model3dSection}>
                    <div className={styles.model3dIcon}>🎮</div>
                    <p>This build has a 3D model</p>
                    <Link to={`/3d-preview?config=${id}`} className={styles.view3dBtn}>
                        View in 3D
                    </Link>
                </div>
            )}

            {/* 分享区域 */}
            <div className={styles.shareSection}>
                <div className={styles.qrWrapper}>
                    <QRCodeSVG
                        value={pageUrl}
                        size={100}
                        level="H"
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                    />
                </div>
                <div className={styles.shareActions}>
                    <p className={styles.shareText}>Share this build</p>
                    <button className={styles.shareBtn} onClick={handleShare}>
                        Copy Link
                    </button>
                </div>
            </div>

            {/* CTA 按钮 */}
            <div className={styles.ctaSection}>
                {/* 继续定制按钮 - 使用当前配置 */}
                <button
                    className={styles.continueBtn}
                    onClick={handleContinueCustomizing}
                >
                    <span className={styles.ctaIcon}>🔧</span>
                    继续定制 / Continue Customizing
                </button>

                {/* 从头开始按钮 */}
                <Link to="/3d-preview" className={styles.ctaBtn}>
                    <span className={styles.ctaIcon}>🏍️</span>
                    Build Your Own Fat Boy
                </Link>
            </div>

            {/* 官方徽章 */}
            {config.status === 'approved' && config.is_official && (
                <div className={styles.officialBadge}>
                    ★ Official Selection
                </div>
            )}
        </div>
    );
}
